import { useEffect, useMemo, useState } from "react";
import { Tabs, Tooltip } from "antd";
import SectionPanel from "pages/analytics/components/SectionPanel";
import {
  EmptyMessage,
  ErrorBanner,
  TableSkeleton,
} from "pages/analytics/components/AnalyticsStates";
import { formatCurrency, formatNumber } from "pages/analytics/components/analyticsFormatters";
import { getRevenueByStage } from "services/funnel-analytics.service";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";

const PRIMARY_PIPELINE_NAME = "BV1 Mentorship";

const formatStageName = (stageName) => {
  if (!stageName) return stageName;
  const normalized = String(stageName).trim().toLowerCase();
  if (
    normalized === "disapproved" ||
    normalized === "disapproved lead" ||
    normalized === "notqualified"
  ) {
    return "Not Qualified";
  }
  return stageName;
};

const resolveDefaultPipeline = (pipelineNames) => {
  if (pipelineNames.includes(PRIMARY_PIPELINE_NAME)) {
    return PRIMARY_PIPELINE_NAME;
  }
  return pipelineNames[0] ?? null;
};

export default function RevenueByStageTable({ api, filters }) {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPipeline, setSelectedPipeline] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setRows([]);
      setError(null);
      setSelectedPipeline(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getRevenueByStage(api, { filters });
        if (!cancelled) {
          setRows(result?.stages ?? []);
        }
      } catch {
        if (!cancelled) {
          setRows([]);
          setError("Failed to load revenue by stage. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [filters.dateRangeType, filters.customDateRange?.[0], filters.customDateRange?.[1]]);

  const pipelineNames = useMemo(() => {
    const names = [];
    const seen = new Set();
    for (const row of rows) {
      const name = row.pipelineName;
      if (!name || seen.has(name)) continue;
      seen.add(name);
      names.push(name);
    }
    return names;
  }, [rows]);

  useEffect(() => {
    if (pipelineNames.length === 0) {
      setSelectedPipeline(null);
      return;
    }
    setSelectedPipeline((current) => {
      if (current && pipelineNames.includes(current)) return current;
      return resolveDefaultPipeline(pipelineNames);
    });
  }, [pipelineNames]);

  const filteredRows = useMemo(() => {
    if (!selectedPipeline) return [];
    return rows.filter((row) => row.pipelineName === selectedPipeline);
  }, [rows, selectedPipeline]);

  const selectedTotals = useMemo(
    () =>
      filteredRows.reduce(
        (acc, row) => ({
          crmValue: acc.crmValue + Number(row.crmValue || 0),
          actualRevenue: acc.actualRevenue + Number(row.actualRevenue || 0),
        }),
        { crmValue: 0, actualRevenue: 0 }
      ),
    [filteredRows]
  );

  const showPipelineTabs = pipelineNames.length > 1;

  return (
    <SectionPanel title="Revenue By Stage">
      {error && <div className="mb-4"><ErrorBanner message={error} /></div>}

      {isLoading ? (
        <TableSkeleton rows={6} cols={5} />
      ) : isCustomRangeIncomplete(filters) ? (
        <EmptyMessage message="Select start and end dates to view revenue by stage." />
      ) : error ? (
        <EmptyMessage message="Revenue by stage unavailable due to a loading error." />
      ) : rows.length === 0 ? (
        <EmptyMessage message="No revenue by stage data for the selected period." />
      ) : (
        <>
          {showPipelineTabs && (
            <Tabs
              activeKey={selectedPipeline ?? undefined}
              onChange={setSelectedPipeline}
              items={pipelineNames.map((name) => ({
                key: name,
                label: name,
              }))}
              className="mb-2"
            />
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Pipeline
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Stage
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Count
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    <Tooltip title="Estimated value if all deals in this stage closed.">
                      <span className="cursor-help border-b border-dotted border-gray-400">
                        CRM Value
                      </span>
                    </Tooltip>
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Actual Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRows.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 text-sm text-gray-900">
                      {row.pipelineName}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-900">
                      {formatStageName(row.stageName)}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-900 text-right">
                      {formatNumber(row.count)}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-900 text-right">
                      {formatCurrency(row.crmValue)}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-900 text-right">
                      {formatCurrency(row.actualRevenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {filteredRows.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 font-medium">
                    <td colSpan={3} className="px-3 py-2.5 text-sm text-gray-700">
                      Total
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-900 text-right">
                      {formatCurrency(selectedTotals.crmValue)}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-900 text-right">
                      {formatCurrency(selectedTotals.actualRevenue)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </SectionPanel>
  );
}
